import { DataSource } from '@angular/cdk/collections';
import { HttpClient } from '@angular/common/http';
import { CollectionViewer } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DomSanitizer } from '@angular/platform-browser';
import Dropzone from 'dropzone';
import 'firebase/storage';
import { difference, orderBy } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, filter, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { ServiceEventTypes } from '../../enums';
import { Response, Service } from '../../models';
import { AuthenticationService, DialogService, JobsService, ProcessorService, SnackbarService } from '../../services/standard';
import { JobUploaderService } from '../../services/standard/job-uploader.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { JobUploadItemComponent } from './job-upload-item/job-upload-item.component';

@Component({
  selector: 'prism-uploader-modal',
  templateUrl: './uploader-modal.component.html',
  styleUrls: ['./uploader-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class UploaderModalComponent implements OnInit, OnDestroy, AfterViewInit {
  subDestroyer$ = new Subject();
  deliveryRef: {
    [serviceId: string]: {
      lastDateDelivered: string,
      canDeliver: boolean
    }
  } = {};

  searchAddressListener = new BehaviorSubject(null);
  serviceUploadDeleteInProgress = {};
  deleteSelectionCollection = {};
  services = new ServicesDataSource(this.jobsService, this.changeRef, this.processorService);
  previewContainerArray = [];
  globalDeliveryPending = false;
  JobUploaded = false;
  localRefSelectedJob;
  allJobs$: Observable<any[]> = this.jobsService.jobs$
    .pipe(
      map(jobs => orderBy(jobs, job => job.received, 'desc'))
    );
  allJobsSub: Subscription;
  fileDropzone: Dropzone;
  allUploadsSub: Subscription;

  jobDeliverPendingTracker = {};

  @ViewChild('dzPreview', {static: true}) dzPreview: ElementRef;
  @ViewChild('dropzone', {static: true}) dropzone: ElementRef<HTMLDivElement>;
  @ViewChild('dropzoneMessage', {static: true}) dropzoneMessage: ElementRef;
  @ViewChildren('thumbnail') thumbnails: ElementRef[];
  @ViewChildren('uploadItems') uploadItems: QueryList<JobUploadItemComponent>;
  @ViewChild('uploadImagesContainer') uploadImagesContainer: ElementRef<HTMLDivElement>;

  dZoneConfig: Dropzone.DropzoneOptions = {
    url: '#',
    method: 'PUT',
    autoQueue: true,
    maxThumbnailFilesize: 60,
    uploadMultiple: true,
    autoProcessQueue: false,
    createImageThumbnails: true,
    chunking: false
  };
  searchTerm: string;

  @ViewChild('uploaderItemPane', {static: true}) uploaderItemPane: ElementRef<HTMLDivElement>;
  @ViewChild('searchAddressInput', {static: true}) searchAddressInput: ElementRef<HTMLInputElement>;

  constructor(private jobsService: JobsService,
              private processorService: ProcessorService,
              private authService: AuthenticationService,
              private sanitizer: DomSanitizer,
              private dialogService: DialogService,
              private http: HttpClient,
              private changeRef: ChangeDetectorRef,
              private jobUploaderService: JobUploaderService,
              private snackbarService: SnackbarService) {
  }

  ngOnInit() {
    this.jobUploaderService.currentJobUploader.subscribe(x => {
      this.JobUploaded = x;
    });

    const {job} = this.dialogService.inputData;
    if (job && job?.id) {
      this.setSelectedUploaderJob(job);

      let searchString;
      if (!job?.id) {
        searchString = '';
      }

      if (job?.id.length > 6) {
        const startIndex = job?.id.length - 6;
        searchString = job?.id.substr(startIndex);
      }
      this.services.setSearchString(searchString);
      this.localRefSelectedJob = job;
    } else {
      this.allJobsSub = this.allJobs$.subscribe(
        jobs => {
          if (jobs[0] !== null) {
            const sortedJobs = jobs.sort((a, b) => {
              return +b.received - +a.received;
            });
            this.setSelectedUploaderJob(sortedJobs[0]);
            this.localRefSelectedJob = sortedJobs[0];
          }
        },
        err => console.error(err),
        () => this.allJobsSub.unsubscribe()
      );
    }

    this.searchAddressListener
      .pipe(
        distinctUntilChanged(),
        filter(text => {
          if (text) {
            return text.length > 1;
          }
          return false;
        }),
        switchMap((searchString) => {
          const searchTags = searchString
            .trim()
            .toLowerCase()
            .replace(/[^a-zA-Z0-9_\-/]+/g, ' ')
            .split(' ')
            .filter(term => !!term);

          return this.jobsService.getSearchedServiceDetails({searchTags})
            .pipe(
              switchMap((servicesResponse: Response) => {
                const services = Object.values(servicesResponse.data);
                this.searchAddressInput.nativeElement.value = searchString;
                this.services.setSearchResults(services, searchString);

                this.setSelectedUploaderJob(services[0]);
                const finalDirectories = Object.values(servicesResponse.data).map((service: any) => service.fromDirectory);
                return this.jobsService.getServicesFinals({finalDirectories})
                  .pipe(
                    map(urlsResponse => {
                      return [servicesResponse, urlsResponse];
                    })
                  );
              })
            );
        })
      )
      .subscribe(([servicesResponse, urlsResponse]) => {
        const services = servicesResponse.data;
        const finals = urlsResponse.data;
        this.services.setSearchResultFinals(services, finals);
      });
  }

  ngAfterViewInit() {

    const {job} = this.dialogService.inputData;

    if (job && job?.id) {

      let searchString;
      if (!job?.id) {
        searchString = '';
      }

      if (job?.id.length > 6) {
        const startIndex = job?.id.length - 6;
        searchString = job?.id.substr(startIndex);
      }
      this.searchAddressListener.next(searchString);

    } else {
      this.services.requestViewUpdate$
        .subscribe((service) => {
          this.setSelectedUploaderJob(service);
        });
    }

    fromEvent(this.searchAddressInput.nativeElement, 'keyup')
      .pipe(
        takeUntil(this.subDestroyer$),
        map((e: KeyboardEvent) => (e.target as HTMLInputElement).value),
        debounceTime(500),
        distinctUntilChanged(),
        filter(text => {
          return text.length > 1;
        })
      )
      .subscribe(() => {
        this.searchAddressListener.next(this.searchAddressInput.nativeElement.value);
      });

    this.dZoneConfig.addedfile = (file: Dropzone.DropzoneFile) => {
      const {address, fromDirectory, id: jobId} = this.localRefSelectedJob;
      const street = address.substr(0, address.indexOf(',')).split(' ').join('_');
      file['jobId'] = jobId;
      file['fromDirectory'] = fromDirectory;
      file['street'] = street;
    };
    this.fileDropzone = new Dropzone(this.dropzone.nativeElement, this.dZoneConfig);
    this.fileDropzone.accept = ((file: any, done) => {
      const fileType = file.type;
      const isPdfFile = fileType.includes('pdf');
      const isAiFile = fileType.includes('postscript');
      const isMiscType =
        fileType.includes('video')
        || (fileType.includes('image') && !fileType.includes('jpeg') && !fileType.includes('png'))
        || (fileType.includes('photoshop'))
        || (!fileType.includes('video') && !fileType.includes('image') && !isAiFile && !isPdfFile);

      if (isMiscType) {
        this.handleFileUpload(file, '../../../assets/images/no_image.jpg');
      } else if (isPdfFile) {
        this.handleFileUpload(file, '../../../assets/images/pdf-logo.jpeg');
      } else if (isAiFile) {
        this.handleFileUpload(file, '../../../assets/images/ai-logo.png');
      } else {
        this.handleFileUpload(file);
      }

      done();
    });
    this.fileDropzone.on('dragover', data => this.dropzone.nativeElement.classList.add('drag-over'));
    this.fileDropzone.on('dragleave', data => this.dropzone.nativeElement.classList.remove('drag-over'));
    this.fileDropzone.on('drop', (dragEvent: DragEvent) => this.dropzone.nativeElement.classList.remove('drag-over'));
  }

  deliverJob() {
    const jobToUpload = this.uploadItems.find(job => job.isSelectedJob);
    jobToUpload.deliverJob();
    if (!this.JobUploaded) {
      this.JobUploaded = true;
      this.jobUploaderService.updateJobUploaded(true);
    }
  }

  ngOnDestroy() {
    if (this.allUploadsSub) {
      this.allUploadsSub.unsubscribe();
    }

    this.subDestroyer$.next(null);
  }

  setSelectedUploaderJob(job) {
    this.previewContainerArray.forEach((previewContainer: HTMLDivElement) => {
      previewContainer.hidden = !previewContainer.classList.contains(`${job.id}`);
    });
    this.localRefSelectedJob = job;
    this.jobUploaderService.setSelectedUploaderJob(job);
    this.services.setSelectedService(job);
  }

  closeDialog() {
    this.dialogService.closeDialog();
  }

  setJobDeliverPending(jobDeliveryPending: { [jobId: string]: boolean }) {
    const deliveryKey = Object.keys(jobDeliveryPending)[0];
    this.jobDeliverPendingTracker[deliveryKey] = jobDeliveryPending[deliveryKey];
    const deliveryValues = Object.values(this.jobDeliverPendingTracker);
    const deliveryBusy = deliveryValues.find(item => item === true);
    this.globalDeliveryPending = !!deliveryBusy;
    this.JobUploaded = true;
    this.jobUploaderService.updateJobUploaded(true);
  }

  makeTrustedImage(item) {
    const imageString = JSON.stringify(item).replace(/\\n/g, '');
    const style = 'url(' + imageString + ')';
    return this.sanitizer.bypassSecurityTrustStyle(style);
  }

  handleFileUpload(file: any, thumbUrl?: string) {
    const originalJob = this.localRefSelectedJob;
    this.jobUploaderService.setCurrentUpload(null, 0, false, file.name, file.upload.uuid, file.jobId);
    const previewContainer = document.createElement('div');
    const previewImg = document.createElement('div');
    previewContainer.classList.add(`${file.jobId}`);
    const progressElement = <HTMLProgressElement>document.createElement('progress');

    progressElement.max = 100;
    previewContainer.classList.add('preview-container');
    previewImg.classList.add('preview-img');
    previewContainer.classList.add('preview-uploading');
    previewContainer.insertAdjacentElement('beforeend', previewImg);
    previewContainer.insertAdjacentElement('beforeend', progressElement);

    previewContainer.hidden = !previewContainer.classList.contains(`${this.localRefSelectedJob.id}`);

    this.uploadImagesContainer.nativeElement.insertAdjacentElement('beforeend', previewContainer);
    this.previewContainerArray.push(previewContainer);

    const fileStorageDestination = `${this.localRefSelectedJob.id}_finals/${moment().valueOf()}_${file.name}`;

    const uploadTask = uploadBytesResumable(ref(getStorage(), fileStorageDestination), file, {
      customMetadata: {
        processingStatus: 'process',
        processorUid: this.authService.currentProcessorUid
      }
    });

    uploadTask.on('state_changed', (snapshot) => {
      const uploadProgress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
      progressElement.value = uploadProgress;
      this.jobUploaderService.setCurrentUpload(uploadTask, uploadProgress, false, file.name, file.upload.uuid, file.jobId);
      switch (snapshot.state) {
        case 'paused':
          break;
        case 'running':
          break;
        case 'success':
          break;
      }
    }, (error) => {
      console.log(error);
    }, () => {
      previewContainer.remove();

      if (!originalJob.imagesToDeliver) {
        originalJob.imagesToDeliver = [];
      }

      if (!thumbUrl) {
        this.makeTrustedImage(file.dataURL);
        let newImageDataToAdd = {
          url: file.dataURL,
          finalPath: uploadTask.snapshot.ref.fullPath,
          originalContentType: file.type,
          originTimestamp: moment().utc().format()
        };
        this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);

      } else {
        this.makeTrustedImage(thumbUrl);
        let newImageDataToAdd = {
          url: thumbUrl,
          finalPath: uploadTask.snapshot.ref.fullPath,
          originalContentType: file.type,
          originTimestamp: moment().utc().format()
        };
        this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);
      }

      this.changeRef.markForCheck();
      this.JobUploaded = false;
      this.jobUploaderService.updateJobUploaded(false);
      previewContainer.removeChild(progressElement);
      previewContainer.classList.remove('preview-uploading');
      this.jobUploaderService.setCurrentUpload(uploadTask, 100, true, file.name, file.upload.uuid, file.jobId);
    });
  }

  deleteSelectedUploads() {
    const dialogRef = this.dialogService.openDialog(ConfirmModalComponent, {confirmMessage: 'Delete selected uploads?'}, {panelClass: 'base-modal-dialog'});

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.snackbarService.showSnackbar('Deleting uploads...');
        this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = true;
        this.changeRef.markForCheck();
        const toDeleteFileFinalPaths = this.deleteSelectionCollection[this.localRefSelectedJob.id]
          .map(fileData => ({finalPath: fileData.finalPath}));

        this.jobUploaderService.deleteUploads(toDeleteFileFinalPaths)
          .toPromise()
          .then(() => {

            this.services.setServiceImagesToDeliver(this.localRefSelectedJob.id,
              this.deleteSelectionCollection[this.localRefSelectedJob.id],
              true);

            this.deleteSelectionCollection[this.localRefSelectedJob.id] = [];
            this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = false;
            this.changeRef.markForCheck();
          })
          .catch(e => {
            console.error('Could not delete file:', e);
            this.snackbarService.handleError('Could not delete file');
            this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = false;
          });
      }
    });


  }

  toggleUploadSelect(uploadFileData: any, changeEvent: MatCheckboxChange) {
    if (changeEvent.checked) {

      if (!this.deleteSelectionCollection[this.localRefSelectedJob.id]) {
        this.deleteSelectionCollection[this.localRefSelectedJob.id] = [];
      }

      this.deleteSelectionCollection[this.localRefSelectedJob.id].push(uploadFileData);
    } else {
      this.deleteSelectionCollection[this.localRefSelectedJob.id] = this.deleteSelectionCollection[this.localRefSelectedJob.id]
        .filter((fileItem) => {
          return fileItem.finalPath !== uploadFileData.finalPath;
        });
    }
  }

  setCanDeliver(event: { id: string, lastDateDelivered: string; canDeliver: boolean }) {
    const deliveryObjCopy = this.deliveryRef;

    deliveryObjCopy[event.id] = {
      canDeliver: event.canDeliver,
      lastDateDelivered: event.lastDateDelivered
    };

    this.deliveryRef = deliveryObjCopy;
  }

}

export class ServicesDataSource extends DataSource<(Service | undefined)> {
  private _length = 0;
  private _pageSize = 40;
  private _cachedData = [];
  private _fetchedPages = new Set<number>();
  private _dataStream = new BehaviorSubject<(Service | undefined)[]>(this._cachedData);
  private _subscription = new Subscription();
  thumbsLoaded = false;
  selectedService = null;
  searchString = '';
  requestViewUpdate$: EventEmitter<Service> = new EventEmitter();

  constructor(private jobsService: JobsService,
              private changeDetector: ChangeDetectorRef,
              private processorService: ProcessorService) {
    super();
    this.processorService.processor$
      .subscribe(processor => {
        this._length = processor.jobsCount;
        this._cachedData.push({});
      });
  }

  setServiceImagesToDeliver(serviceId, imageData, remove = false) {
    const foundSearchedServiceIndex = this._dataStream.value.findIndex(job => {
      return job.id === serviceId;
    });

    // STANDARD RESULTS THAT SHOULD BE SCANNED
    const foundCachedServiceIndex = this._cachedData.findIndex(job => {
      return job.id === serviceId;
    });

    if (remove) {
      // SEARCH RESULTS FILE COMPARISON
      if (!!this._dataStream.value[foundSearchedServiceIndex]) {
        this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver =
          difference(this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver, imageData);
        this._dataStream.next(this._dataStream.value.sort((a, b) => +b.received - +a.received));
      }

      // STANDARD RESULTS FILE COMPARISON
      if (!!this._cachedData[foundCachedServiceIndex]) {
        this._cachedData[foundCachedServiceIndex].imagesToDeliver =
          difference(this._cachedData[foundCachedServiceIndex].imagesToDeliver, imageData);
        this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
      }

      if (!!this.searchString) {
        if (!!this._cachedData[foundCachedServiceIndex]) {
          this._dataStream.value[foundSearchedServiceIndex] = this._cachedData[foundCachedServiceIndex];
        }

        this._dataStream.next(this._dataStream.value.sort((a, b) => +b.received - +a.received));
      } else {


        this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
      }

    } else {
      // STANDARD RESULTS FILE ADD
      if (!!this._cachedData[foundCachedServiceIndex]) {
        this._cachedData[foundCachedServiceIndex].imagesToDeliver.push(imageData);
      } else if (this._dataStream.value[foundSearchedServiceIndex]) {
        this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver.push(imageData);
      }
    }

    if (this._cachedData[foundCachedServiceIndex]) {
      if (this.selectedService.id === this._cachedData[foundCachedServiceIndex].id) {
        this.selectedService.imagesToDeliver = this._cachedData[foundCachedServiceIndex].imagesToDeliver;
        this.selectedService.images = this._cachedData[foundCachedServiceIndex].images;
      }
    } else if (this.selectedService.id === this._dataStream.value[foundSearchedServiceIndex].id) {
      this.selectedService.imagesToDeliver = this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver;
      this.selectedService.images = this._dataStream.value[foundSearchedServiceIndex].images;
    }

    this.requestViewUpdate$.emit(this.selectedService);
  }

  connect(collectionViewer: CollectionViewer, initial = false): Observable<(Service | undefined)[]> {
    this._subscription.add(collectionViewer.viewChange.subscribe(range => {
      const startPage = this._getPageForIndex(range.start);
      const endPage = this._getPageForIndex(range.end - 1);
      for (let i = startPage; i <= endPage; i++) {
        this._fetchPage(i);
      }
    }));
    return this._dataStream;
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  setSelectedService(service) {
    this.selectedService = service;
    this.changeDetector.markForCheck();
  }

  private _getPageForIndex(index: number): number {
    return Math.ceil(index / this._pageSize);
  }

  private _fetchPage(page: number) {
    if (!this.searchString.length) {
      if (this._fetchedPages.has(page)) {
        return;
      }
      this._fetchedPages.add(page);
      this.makeServicesApiCall(page);
    }
  }

  setSearchString(searchString) {
    this.searchString = searchString;

  }

  setSearchResults(services, searchString) {
    this.searchString = searchString;
    if (services.length) {
      this._dataStream.next(services);
    } else if (!services.length && !!searchString) {
      this._dataStream.next([]);
    } else {
      this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
    }
  }

  setSearchResultFinals(services, finals) {
    if (finals) {
      const lastDeliveredObject = {};
      Object.entries(services).forEach(([serviceId, service]) => {
        const timelineEvents = Object
          .entries((<Service>service).timelineEvents || {
            [(<any>service).received]: {
              eventType: ServiceEventTypes.CREATION, date: (<any>service).received
            }
          });
        let lastDateDeliveredEntry = timelineEvents.reduce((acc, event) => {

          if ((<any>event[1]).eventType === ServiceEventTypes.DELIVERY) {

            if (!acc || moment(+acc[0]).isBefore(moment(+(<any>event[1]).date))) {
              acc = event;
            }
          }

          return acc;
        }, null);


        if (!lastDateDeliveredEntry) {
          lastDateDeliveredEntry = timelineEvents[0];
        }
        lastDeliveredObject[serviceId] = +lastDateDeliveredEntry[0];

      });

      Object.entries(finals).forEach(([serviceId, urls]) => {

        const found = this._dataStream.value.find(service => {
          if (!!service) {
            return service.id === serviceId;
          }
          return false;
        });

        if (!!found) {
          const [images, imagesToDeliver] = (<{ data, url }[]>urls).reduce((acc, urlItem) => {
            const assetNotDelivered = urlItem.data.notDelivered;

            if (assetNotDelivered) {
              acc[1].push({
                url: urlItem.url,
                ...urlItem.data
              });
            } else {
              acc[0].push({
                url: urlItem.url,
                ...urlItem.data
              });
            }

            return acc;
          }, [[], []]);

          found.images = images;
          found.imagesToDeliver = imagesToDeliver;

          this.thumbsLoaded = true;
        }

        this.changeDetector.detectChanges();
      });
    }
  }

  makeServicesApiCall(page, initial = false) {
    const source = this.jobsService.getServicesDetails({pageSize: this._pageSize, pageIndex: page});

    const final = source
      .pipe(
        switchMap(servicesResponse => {
          if (page === 0) {
            this._cachedData = [];
          }

          const jobs = Object.values(servicesResponse.data);
          this._cachedData = this._cachedData.concat(jobs);

          if (page === 0) {
            this.selectedService = this._cachedData[0];
          }
          if (!this.searchString) {
            this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
          }

          const finalDirectories = Object.values(servicesResponse.data).map((service: any) => service.fromDirectory);
          return this.jobsService.getServicesFinals({finalDirectories})
            .pipe(
              map(urlsResponse => {
                return [servicesResponse, urlsResponse];
              })
            );
        })
      );

    final.subscribe(
      ([servicesResponse, urlsResponse]) => {
        const lastDeliveredObject = {};
        Object.entries(servicesResponse.data).forEach(([serviceId, service]) => {

          const timelineEvents = Object.entries((<Service>service).timelineEvents || {
            [(<any>service).received]: {
              eventType: ServiceEventTypes.CREATION, date: (<any>service).received
            }
          });

          let lastDateDeliveredEntry = timelineEvents.reduce((acc, event) => {

            if ((<any>event[1]).eventType === ServiceEventTypes.DELIVERY) {

              if (!acc || moment(+acc[0]).isBefore(moment(+(<any>event[1]).date))) {
                acc = event;
              }
            }

            return acc;
          }, null);


          if (!lastDateDeliveredEntry) {
            lastDateDeliveredEntry = timelineEvents[0];
          }
          lastDeliveredObject[serviceId] = +lastDateDeliveredEntry[0];

        });

        Object.entries(urlsResponse.data).forEach(([serviceId, urls]) => {
          const found = this._cachedData.find(service => {
            return service.id === serviceId;
          });

          const [images, imagesToDeliver] = (<{ data, url }[]>urls).reduce((acc, urlItem) => {
            const assetNotDelivered = urlItem.data.notDelivered;
            if (assetNotDelivered) {
              acc[1].push({
                url: urlItem.url,
                ...urlItem.data
              });
            } else {
              acc[0].push({
                url: urlItem.url,
                ...urlItem.data
              });
            }

            return acc;
          }, [[], []]);

          if (found) {
            found.images = images;
            found.imagesToDeliver = imagesToDeliver;
          }

          this.thumbsLoaded = true;
        });

        if (!initial && !this.searchString) {
          this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
        }
        this.changeDetector.detectChanges();
      },
      err => {
        console.error('Error with finals subscription:', err);
      },
      () => {
      }
    );


  }
}
